import {AfterViewInit, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import finalizeSteps from './outline-generation-finalize.json';
import * as fromStore from "../../../../../store";
import {filter, first, switchMap, takeUntil} from "rxjs/operators";
import {Store} from "@ngrx/store";
import {UserService} from "../../../../../user/user.service";
import {combineLatest, Subject} from "rxjs";
import {BuilderSectionsInteractionService} from "../../../../builder-sections-interaction.service";
import {DbModalService} from "../../../../../modules/modal/modal";
import {User} from "@sentry/browser";
import {GoogleAnalyticsService} from "../../../../../shared/services/google-analytics/google-analytics.service";
import {ActivatedRoute} from "@angular/router";
import {BuilderHistoryService} from "../../../../builder-history.service";
import {DbActiveModal} from "../../../../../modules/modal/modal-ref";

type createAIFinalizeStepStatus = 'todo' | 'process' | 'passed';

interface ICreateAIFinalizeStep {
  label: string;
  description?: string;
  status: createAIFinalizeStepStatus | string;
  isEcommerce?: boolean;
}

@Component({
    selector: 'db-outline-generation-finalize',
    templateUrl: './outline-generation-finalize.component.html',
    styleUrls: ['./outline-generation-finalize.component.scss'],
    standalone: false
})
export class OutlineGenerationFinalizeComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() steps: ICreateAIFinalizeStep[];
  @Input() stepTime: number = 10000;
  @Input() popupMode: boolean;
  @Input() singlePage: boolean;
  @Input() user: User;
  @Input() uniqueId: string;
  @Input() isEcommerce: boolean = false;

  userLocalStorage: any;
  updateLocalStorage: boolean = false;
  loading: boolean = false;
  isMobile: boolean = false;
  private counter: number = -1;
  private interval: ReturnType<typeof setInterval>;
  private _destroyStream$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private _userService: UserService,
    private _store: Store<fromStore.DbStoreState>,
    public builderInteractionsService: BuilderSectionsInteractionService,
    public _builderHistoryService: BuilderHistoryService,
    public _modalService: DbModalService,
    private _cdr: ChangeDetectorRef,
    private _googleAnalyticsService: GoogleAnalyticsService,
    private _route: ActivatedRoute,
    private _activeModal: DbActiveModal,
  ) {
    this.isEcommerce = this.builderInteractionsService.getWebsiteTypeMobile() === 'ecommerce' || this.builderInteractionsService.websiteType.value === 'ecommerce';
    this.uniqueId = this._builderHistoryService.getUniqueId();
  }

  ngOnInit() {
    if (window.innerWidth <= 991) {
      this.isMobile = true;
    }
    const _steps = finalizeSteps;
    this.steps = this.singlePage ? this.steps : _steps.filter(step => step['isEcommerce'] === this.isEcommerce || step['isEcommerce'] === undefined);
    this._subscribeToLoading();
    this._getUserLocalStorage();
    this._initSteps();
    this._toNextStep(this.stepTime);
    if (this.popupMode) {
      this._activeModal.hideOutsideCloseBtn(true);
    }
  }

  ngOnDestroy() {
    clearInterval(this.interval);
    this._destroyStream$.next(true);
    this._destroyStream$.complete();
  }

  ngAfterViewInit() {
    this._cdr.detectChanges();
  }

  private _subscribeToLoading(): void {
    this.builderInteractionsService.buildLoading
      .pipe(takeUntil(this._destroyStream$))
      .subscribe(isLoading => {
        this.loading = isLoading;
        if (!isLoading && this.steps.at(-1).status === 'process') {
          this.steps.at(-1).status = 'passed';
        }
        this._cdr.markForCheck();
      });
  }

  private _initSteps() {
    this.steps.map((step, index) => {
      step.status = index === 0 ? "process" : "todo";
    });
    this._cdr.markForCheck();
  }

  private _checkSuggestedDomain(domainName) {
    this.userLocalStorage['suggestedDomainNameInitial'] = localStorage.getItem('dbOnboardingDomainNameInitial');
    this.userLocalStorage['suggestedDomainName'] = domainName;
    this._updateLocalStorage();
  }

  private _getUserLocalStorage(): void {
    this._store.select(fromStore.getUserLoaded)
      .pipe(
        filter((loaded: boolean) => loaded),
        switchMap(() => combineLatest(
          this._store.select(fromStore.getUserLocalStorage),
          this._store.select(fromStore.getUserMeta()),
        )),
        takeUntil(this._destroyStream$)
      )
      .subscribe(([userLocalStorage, meta]) => {

        this.userLocalStorage = userLocalStorage;
        !this.updateLocalStorage && this._updateUserLocalStorage();
      });
  }

  private _updateUserLocalStorage(): void {
    if (localStorage.getItem('dbOnboardingDomainName')) {
      this._checkSuggestedDomain(localStorage.getItem('dbOnboardingDomainName'));
      localStorage.removeItem('dbOnboardingDomainName');
      localStorage.removeItem('dbOnboardingDomainNameInitial');
      localStorage.removeItem('dbOnboardingDomainInfo');
    } else {
      this._updateLocalStorage();
    }
  }

  private _updateLocalStorage(): void {
    this._userService.setToLocal(this.userLocalStorage)
      .pipe(
        first(response => !!response, null),
      )
      .subscribe(response => {
        if (response.success) {
          this.updateLocalStorage = true;
          this._store.dispatch(new fromStore.LoadUserLocalStorage());
          this._store.dispatch(new fromStore.LoadUser());
        }
      });
  }

  private _toNextStep(time): void {
    this.interval = setInterval(() => {
      this.counter++;
      const stepsLength = this.steps.length - 1;
      if (this.counter > stepsLength) this.counter = stepsLength;
      if (this.counter !== stepsLength || this.builderInteractionsService.buildLoading.value) {
        if (this.counter === stepsLength) {
          this.steps[this.counter].status = "process";
        } else {
          this.steps[this.counter].status = "passed";
        }
      }
      if (this.counter === stepsLength) {
        clearInterval(this.interval);
        return;
      }
      this.steps[this.counter + 1].status = "process";
      this._cdr.markForCheck();
    }, time);
  }
}
